import React from 'react';
import { CommandCenterLayout } from '../../components/layout/CommandCenterLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { StatCard } from '../../components/ui/StatCard';
import { Calendar, Users, Share2, CheckCircle2, Clock, AlertCircle, Download, Edit, Trash2, ArrowLeft, Instagram, Facebook, Youtube, Twitter } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
export function CampaignDetailPage() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  // Mock Data
  const campaign = {
    id: id || 'CMP-001',
    title: 'Kampanye HPSN 2025: Pilah Sampah dari Rumah',
    description: 'Kampanye nasional dalam rangka Hari Peduli Sampah Nasional 2025. Fokus pada edukasi pemilahan sampah organik dan anorganik di level rumah tangga.',
    status: 'active',
    startDate: '2025-02-10',
    deadline: '2025-02-21',
    platforms: ['instagram', 'tiktok', 'facebook'],
    receivers: 514,
    stats: {
      progress: 68,
      submitted: 350,
      validated: 320,
      rejected: 15,
      pending: 164
    },
    assets: [{
      type: 'video',
      name: 'Video Teaser HPSN.mp4',
      size: '15 MB'
    }, {
      type: 'image',
      name: 'Infografis Pilah Sampah.jpg',
      size: '2.5 MB'
    }]
  };
  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'instagram':
        return <Instagram className="h-4 w-4 text-pink-600" data-id="element-752" />;
      case 'facebook':
        return <Facebook className="h-4 w-4 text-blue-600" data-id="element-753" />;
      case 'youtube':
        return <Youtube className="h-4 w-4 text-red-600" data-id="element-754" />;
      case 'twitter':
        return <Twitter className="h-4 w-4 text-sky-500" data-id="element-755" />;
      case 'tiktok':
        return <span className="text-xs font-bold" data-id="element-756">TT</span>;
      default:
        return <Share2 className="h-4 w-4" data-id="element-757" />;
    }
  };
  return <CommandCenterLayout data-id="element-758">
      <div className="space-y-6" data-id="element-759">
        {/* Header */}
        <div className="flex items-center gap-4 mb-2" data-id="element-760">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-id="element-761">
            <ArrowLeft className="h-6 w-6" data-id="element-762" />
          </Button>
          <div data-id="element-763">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-764">
              {campaign.title}
            </h1>
            <div className="flex items-center gap-3 text-sm text-gray-500 mt-1" data-id="element-765">
              <span className="flex items-center gap-1" data-id="element-766">
                <Calendar className="h-4 w-4" data-id="element-767" /> {campaign.startDate} -{' '}
                {campaign.deadline}
              </span>
              <span data-id="element-768">•</span>
              <StatusBadge status="hijau" data-id="element-769">Active</StatusBadge>
            </div>
          </div>
          <div className="ml-auto flex gap-2" data-id="element-770">
            <Button variant="outline" className="gap-2" data-id="element-771">
              <Edit className="h-4 w-4" data-id="element-772" /> Edit
            </Button>
            <Button variant="outline" className="gap-2 text-red-600 hover:bg-red-50 border-red-200" data-id="element-773">
              <Trash2 className="h-4 w-4" data-id="element-774" /> Hapus
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-775">
          <StatCard title="Total Penerima" value={campaign.receivers.toString()} icon={Users} data-id="element-776" />
          <StatCard title="Sudah Posting" value={campaign.stats.submitted.toString()} icon={CheckCircle2} description={`${campaign.stats.progress}% completion`} data-id="element-777" />
          <StatCard title="Validated" value={campaign.stats.validated.toString()} icon={CheckCircle2} className="border-green-200 bg-green-50" data-id="element-778" />
          <StatCard title="Pending" value={campaign.stats.pending.toString()} icon={Clock} className="border-yellow-200 bg-yellow-50" data-id="element-779" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-780">
          {/* Left: Details */}
          <div className="lg:col-span-2 space-y-6" data-id="element-781">
            <Card data-id="element-782">
              <CardHeader data-id="element-783">
                <CardTitle data-id="element-784">Deskripsi & Instruksi</CardTitle>
              </CardHeader>
              <CardContent data-id="element-785">
                <p className="text-gray-700 leading-relaxed mb-6" data-id="element-786">
                  {campaign.description}
                </p>

                <h3 className="font-medium text-gray-900 mb-3" data-id="element-787">
                  Target Platform
                </h3>
                <div className="flex gap-2 mb-6" data-id="element-788">
                  {campaign.platforms.map(p => <div key={p} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full border" data-id="element-789">
                      {getPlatformIcon(p)}
                      <span className="capitalize text-sm" data-id="element-790">{p}</span>
                    </div>)}
                </div>

                <h3 className="font-medium text-gray-900 mb-3" data-id="element-791">
                  Materi Campaign
                </h3>
                <div className="space-y-3" data-id="element-792">
                  {campaign.assets.map((asset, idx) => <div key={idx} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50" data-id="element-793">
                      <div className="flex items-center gap-3" data-id="element-794">
                        <div className="h-10 w-10 bg-white rounded border flex items-center justify-center" data-id="element-795">
                          <Share2 className="h-5 w-5 text-gray-400" data-id="element-796" />
                        </div>
                        <div data-id="element-797">
                          <p className="text-sm font-medium text-gray-900" data-id="element-798">
                            {asset.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-799">
                            {asset.type} • {asset.size}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" data-id="element-800">
                        <Download className="h-4 w-4" data-id="element-801" />
                      </Button>
                    </div>)}
                </div>
              </CardContent>
            </Card>

            <Card data-id="element-802">
              <CardHeader className="flex flex-row justify-between items-center" data-id="element-803">
                <CardTitle data-id="element-804">Progress Wilayah</CardTitle>
                <Button variant="outline" size="sm" onClick={() => navigate('/command-center/distribusi/progress')} data-id="element-805">
                  Lihat Detail
                </Button>
              </CardHeader>
              <CardContent data-id="element-806">
                <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center text-gray-400 border border-dashed" data-id="element-807">
                  Chart Progress Wilayah Placeholder
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Timeline & Actions */}
          <div className="space-y-6" data-id="element-808">
            <Card data-id="element-809">
              <CardHeader data-id="element-810">
                <CardTitle data-id="element-811">Timeline</CardTitle>
              </CardHeader>
              <CardContent data-id="element-812">
                <div className="relative border-l-2 border-gray-200 ml-3 space-y-6 pb-2" data-id="element-813">
                  <div className="mb-8 ml-6 relative" data-id="element-814">
                    <span className="absolute -left-[31px] bg-green-500 h-4 w-4 rounded-full border-2 border-white" data-id="element-815"></span>
                    <h3 className="font-medium text-gray-900" data-id="element-816">
                      Campaign Created
                    </h3>
                    <time className="block mb-1 text-sm font-normal text-gray-400" data-id="element-817">
                      10 Feb 2025, 09:00
                    </time>
                  </div>
                  <div className="mb-8 ml-6 relative" data-id="element-818">
                    <span className="absolute -left-[31px] bg-green-500 h-4 w-4 rounded-full border-2 border-white" data-id="element-819"></span>
                    <h3 className="font-medium text-gray-900" data-id="element-820">Published</h3>
                    <time className="block mb-1 text-sm font-normal text-gray-400" data-id="element-821">
                      10 Feb 2025, 10:30
                    </time>
                  </div>
                  <div className="mb-8 ml-6 relative" data-id="element-822">
                    <span className="absolute -left-[31px] bg-blue-500 h-4 w-4 rounded-full border-2 border-white animate-pulse" data-id="element-823"></span>
                    <h3 className="font-medium text-blue-600" data-id="element-824">
                      Distribution Ongoing
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-825">
                      Deadline: {campaign.deadline}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-blue-50 border-blue-100" data-id="element-826">
              <CardContent className="p-4" data-id="element-827">
                <h3 className="font-bold text-blue-900 mb-2" data-id="element-828">Perlu Bantuan?</h3>
                <p className="text-sm text-blue-700 mb-4" data-id="element-829">
                  Jika ada kendala distribusi, hubungi tim teknis atau cek
                  panduan.
                </p>
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-id="element-830">
                  Hubungi Support
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </CommandCenterLayout>;
}